<template>
  <div>
    <template v-if="device.isMobile">
      <v-card elevation="0">
        <v-checkbox
          v-model="showStock"
          hide-details
          dense
          :label="$t('warehouse.show_warehouse_status')"
          class="mt-0 mb-2"
        />
        <v-checkbox
          v-model="showAllGroceries"
          hide-details
          dense
          :label="$t('warehouse.show_all_groceries')"
          class="mt-0 mb-2"
        />
      </v-card>

      <mobile-data-cards
        v-if="translatedInventory.length > 0 || loading"
        :headers="headers"
        :items="translatedInventory"
        :loading="loading"
        show-filters
        :fields-per-row="null"
        :show-row-number="false"
        empty-icon="mdi-clipboard-list-outline"
        :empty-title="$t('no_requirements')"
        :empty-subtitle="$t('no_requirements_yet')"
      >
        <template v-slot:item.order="{ item }">
          <text-field
            color="white"
            hide-details
            :value="item.order"
            @input="updateItemOrder(item, $event)"
            type="number"
            min="0"
            :max="stockType === 'exit' ? item.stock : undefined"
            step="0.001"
            dense
          />
        </template>
        <template v-slot:item.stock="{ item }">
          <span
            :style="{
              backgroundColor: getStockColor(item),
              padding: '2px 6px',
              borderRadius: '4px',
            }"
          >
            {{ item.stock }}
          </span>
        </template>
      </mobile-data-cards>

      <!-- Mobile Bottom Navigation -->
      <v-bottom-navigation fixed grow class="elevation-8 mb-2" :value="stockType">
        <v-btn value="required" text @click="stockType = 'required'">
          <span>{{ $t("warehouse.required") }}</span>
          <v-icon>mdi-clipboard-list-outline</v-icon>
        </v-btn>

        <v-btn value="ordered" text @click="stockType = 'ordered'">
          <span>{{ $t("warehouse.order") }}</span>
          <v-icon>mdi-cart-outline</v-icon>
        </v-btn>

        <v-btn value="exit" text @click="stockType = 'exit'">
          <span>{{ $t("warehouse.issue") }}</span>
          <v-icon>mdi-arrow-right</v-icon>
        </v-btn>

        <!-- Action button based on stockType -->
        <v-menu v-if="stockType === 'ordered'" offset-y top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" color="green accent-4" text>
              <span>{{ $t("order") }}</span>
              <v-icon>mdi-chevron-up</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item @click="orderGroceries(false)">
              <v-list-item-title>{{ $t("warehouse.request_quote") }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="orderGroceries(true)">
              <v-list-item-title>{{ $t("warehouse.purchase_order") }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-btn v-if="stockType === 'exit'" color="blue" text @click="openWayBill()">
          <span>{{ $t("warehouse.release") }}</span>
          <v-icon>mdi-truck-delivery</v-icon>
        </v-btn>
      </v-bottom-navigation>
    </template>

    <v-card elevation="0" v-else>
      <div class="d-flex flex-wrap align-center ga-3">
        <v-radio-group
          v-model="stockType"
          row
          hide-details
          dense
          class="flex-grow-0 mt-0"
        >
          <v-radio :label="$t('warehouse.required')" value="required" />
          <v-radio :label="$t('warehouse.order')" value="ordered" />
          <v-radio :label="$t('warehouse.issue')" value="exit" />
        </v-radio-group>
        <v-checkbox
          v-model="showStock"
          hide-details
          dense
          :label="$t('warehouse.show_warehouse_status')"
          class="flex-grow-0 mt-0"
        />
        <v-checkbox
          v-model="showAllGroceries"
          hide-details
          dense
          :label="$t('warehouse.show_all_groceries')"
          class="flex-grow-0 mt-0"
        />
        <v-spacer class="d-none d-sm-flex" />
        <warehouse-button-order
          v-if="stockType === 'ordered'"
          @order="orderGroceries($event)"
          :small="device.isMobile"
        />
        <v-btn
          v-if="stockType === 'exit'"
          color="blue"
          depressed
          dark
          :small="device.isMobile"
          @click="openWayBill()"
        >
          {{ $t("warehouse.release") }}
        </v-btn>
      </div>
      <v-divider />
      <v-data-table
        v-if="translatedInventory.length > 0 || loading"
        :headers="headers"
        :items="customSearch(translatedInventory, headers, filters.search)"
        :loading="loading"
        class="striped-table ordera-box-shadow"
        :items-per-page="-1"
        hide-default-footer
      >
        <template v-slot:item.num="{ item, index }">
          {{ index + 1 }}
        </template>
        <template v-slot:item.stock="{ item }">
          <td :style="{ backgroundColor: getStockColor(item) }">
            {{ item.stock }}
          </td>
        </template>
        <template v-slot:item.quantity="{ item }">
          {{ formatQuantity(item.quantity) }}
        </template>
        <template v-slot:item.ordered="{ item }">
          {{ item.ordered.toFixed(3) }}
        </template>
        <template v-slot:item.order="{ item }">
          <text-field
            color="white"
            hide-details
            :value="item.order"
            @input="updateItemOrder(item, $event)"
            type="number"
            min="0"
            :max="stockType === 'exit' ? item.stock : undefined"
            step="0.001"
            style="max-width: 100px"
          />
        </template>
        <template v-slot:item.afterActionQuantity="{ item }">
          <td :style="{ backgroundColor: getAfterActionColor(item) }">
            {{ calculateAfterActionQuantity(item) }}
          </td>
        </template>
        <template v-slot:item.created_at="{ item }">
          {{
            localeDate(item.created_at, {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })
          }}
        </template>
      </v-data-table>
    </v-card>

    <v-alert
      v-if="showStockCombinedHint"
      type="info"
      dense
      text
      class="mt-2"
      icon="mdi-information-outline"
    >
      {{ $t("warehouse.stock_combined_hint") }}
    </v-alert>
    <empty-state
      v-if="!loading && translatedInventory.length === 0"
      icon="mdi-clipboard-list-outline"
      :title="$t('no_requirements')"
      :subtitle="$t('no_requirements_yet')"
    />

    <warehouse-purchase-order-dialog
      v-model="purchaseOrderDialog"
      :edited-item="editedItem"
      :is-purchase-order="purchaseOrder"
      @success="load"
    />

    <warehouse-waybill-dialog v-model="waybillDialog" :edited-item="editedItem" />
  </div>
</template>

<script>
import WarehousePurchaseOrderDialog from "@/components/warehouse/dialogs/WarehousePurchaseOrderDialog.vue";
import WarehouseWaybillDialog from "@/components/warehouse/dialogs/WarehouseWaybillDialog.vue";
import groceryService from "@/services/grocery";
import {
  documentGrocery,
  orderDocumentGrocery,
  requestOrderDocumentGrocery,
  waybillDocumentGrocery,
} from "@/models/warehouse";
import { WarehouseButtonOrder } from "@/components/warehouse/buttons";
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasWarehousePrinting from "@/mixins/warehouse/hasWarehousePrinting";
import hasSnackbar from "@/mixins/hasSnackbar";
import warehouseRequirementsMixin from "@/mixins/warehouse/warehouseRequirementsMixin";
import warehouseFormatMixin from "@/mixins/warehouse/warehouseFormatMixin";
import warehouseColorsMixin from "@/mixins/warehouse/warehouseColorsMixin";
import { SHIFT_KEYS, DEFAULT_SHIFTS } from "@/constants/warehouse";

export default {
  mixins: [
    hasPrinting,
    hasWarehousePrinting,
    hasExcel,
    hasSnackbar,
    warehouseRequirementsMixin,
    warehouseFormatMixin,
    warehouseColorsMixin,
  ],
  components: {
    WarehousePurchaseOrderDialog,
    WarehouseWaybillDialog,
    WarehouseButtonOrder,
    EmptyState,
    MobileDataCards,
  },
  data() {
    return {
      purchaseOrderDialog: false,
      editedItem: { ...documentGrocery },
      purchaseOrder: false,
      waybillDialog: false,
      showStock: false,
      showAllGroceries: false,
      allGroceries: [],
      stockType: "required", // 'ordered', 'exit', 'required'
    };
  },
  methods: {
    normalizeOrderValue(value) {
      if (value === "" || value == null) {
        return null;
      }

      const numericValue = Number(value);
      return Number.isNaN(numericValue) ? null : numericValue;
    },
    updateItemOrder(item, value) {
      const normalizedValue = this.normalizeOrderValue(value);

      this.$set(item, "order", normalizedValue);

      const sourceItem = this.inventory.find((inventoryItem) => inventoryItem.id === item.id);
      if (sourceItem) {
        this.$set(sourceItem, "order", normalizedValue);
      }
    },
    openWayBill() {
      if (this.selectedItems.length === 0) {
        this.showErrorSnackbar(this.$t("no_items_selected_for_waybill"));
        return;
      }
      this.editedItem = { ...waybillDocumentGrocery };
      this.editedItem.groceries = this.selectedItemsWithQuantity;
      this.waybillDialog = true;
    },
    orderGroceries(isPurchaseOrder) {
      if (this.selectedItems.length === 0) {
        this.showErrorSnackbar(this.$t("no_items_selected_for_order"));
        return;
      }
      this.purchaseOrder = isPurchaseOrder;
      this.editedItem = isPurchaseOrder
        ? { ...orderDocumentGrocery }
        : { ...requestOrderDocumentGrocery };
      this.editedItem.groceries = this.selectedItemsWithQuantity;
      this.purchaseOrderDialog = true;
    },
  },
  computed: {
    // Selection computed
    selectedItems() {
      return this.translatedInventory.filter((item) => Number(item.order) > 0);
    },
    selectedItemsWithQuantity() {
      return this.selectedItems.map((item) => ({
        ...item,
        amount: Number(item.order),
      }));
    },

    headers() {
      const isActionMode = this.stockType !== "required";
      const isExit = this.stockType === "exit";
      const isGrouped = this.filters.showType !== "daily";
      const selectedShifts = this.filters.selectedShifts || DEFAULT_SHIFTS;

      return [
        { text: this.$t("r_b"), value: "num", mobile: false },
        { text: this.$t("code"), align: "start", value: "code", cols: 3 },
        { text: this.$t("title"), align: "start", value: "name", cols: 9 },

        // Shift headers (dynamic count, kept as spread)
        ...(isGrouped
          ? selectedShifts
              .filter((s) => s >= 1 && s <= 4)
              .map((s) => ({
                value: `shift.${SHIFT_KEYS[s - 1]}`,
                text: `${s}. ${this.$t("warehouse.shift")}`,
                cols: 3,
                suffixField: "measure",
              }))
          : []),

        { text: this.$t("unit_of_measurment"), value: "measure", mobile: false },
        {
          text: this.$t("warehouse.warehouse_status"),
          align: "start",
          value: "stock",
          mobile: false,
          show: this.showStock,
        },
        {
          text: this.$t("warehouse.quantity_based_on_ordered_meals"),
          align: "end",
          value: "quantity",
          mobile: false,
        },
        {
          text: this.$t("warehouse.quantity_for_action", {
            action: isExit ? this.$t("issuing") : this.$t("ordering"),
          }),
          align: "start",
          value: "order",
          sortable: false,
          cols: 6,
          show: isActionMode,
        },
        {
          text: this.$t(isExit ? "warehouse.exit" : "warehouse.ordered"),
          align: "start",
          value: "ordered",
          print: isExit ? false : undefined,
          suffixField: "measure",
          cols: 6,
          show: isActionMode,
        },
        {
          text: this.$t(
            isExit ? "warehouse.quantity_after_issue" : "warehouse.quantity_after_order"
          ),
          align: "start",
          value: "afterActionQuantity",
          mobile: false,
          show: isActionMode,
        },
        { text: "Σ", value: "quantityWithMeasure", desktop: false, cols: 3 },
        {
          text: this.$t("warehouse.warehouse_status"),
          value: "stockWithMeasure",
          desktop: false,
          cols: 12,
          show: this.showStock,
        },
      ].filter((h) => {
        if (h.show === false) return false;
        return this.device.isDesktop ? h.desktop !== false : h.mobile !== false;
      });
    },

    showStockCombinedHint() {
      return (
        this.showStock &&
        this.filters.warehouse.length > 1 &&
        this.translatedInventory.length > 0
      );
    },
    exportTitle() {
      return [
        this.filters.warehouse.map((warehouse) => warehouse.name).join(", ") ||
          this.$t("stock"),
        this.$t(`warehouse.${this.stockType}`).toLowerCase(),
      ].join(" ");
    },
    translatedInventory() {
      const selectedShifts = this.filters.selectedShifts || DEFAULT_SHIFTS;

      const inventoryIds = new Set(this.inventory.map((i) => i.id));

      const extraItems = this.showAllGroceries
        ? this.allGroceries
            .filter((g) => !inventoryIds.has(g.id))
            .map((g) => ({
              id: g.id,
              code: g.code || "",
              name: g.name || "",
              measure: g.measure || "",
              quantity: 0,
              stock: 0,
              order: null,
              ordered: 0,
            }))
        : [];

      return [...this.inventory, ...extraItems]
        .map((item) => {
          const quantity = this.calculateItemQuantity(item, selectedShifts);

          return {
            ...item,
            name: this.toLatin(item.name),
            order: item.order ?? null,
            quantity,
            quantityWithMeasure: `${this.formatQuantity(quantity)} ${item.measure || ""}`,
            stockWithMeasure: `${item.stock || 0} ${item.measure || ""}`,
          };
        })
        .filter((item) => {
          // Za "exit" (izdavanje) - prikazuj samo stavke koje se nalaze u magacinu (stock > 0)
          if (this.stockType === "exit") {
            return parseFloat(item.stock || 0) > 0;
          }
          // Ako je showAllGroceries, prikaži sve bez obzira na quantity
          if (this.showAllGroceries) {
            return true;
          }
          // Namirnice koje nisu dio jela (quantity === 0) vide se samo za "ordered"
          if (item.quantity === 0) {
            return this.stockType === "ordered";
          }
          return true;
        });
    },
    filters() {
      return this.$store.state.warehouse.filters;
    },
    restaurant() {
      return this.$store.state.restaurant || this.$store.state.company.restaurant;
    },
  },
  watch: {
    showAllGroceries(val) {
      if (val && this.allGroceries.length === 0) {
        groceryService.getGroceries().then((res) => {
          this.allGroceries = res.data?.data || res.data || [];
        });
      }
    },
    "filters.warehouse": {
      handler() {
        this.debouncedLoad();
      },
      deep: true,
    },
    "filters.selectedShifts": {
      handler() {
        this.debouncedLoad();
      },
      deep: true,
      immediate: true,
    },
    "filters.dates.from"() {
      this.debouncedLoad();
    },
    "$store.state.warehouse.refreshTrigger"() {
      this.debouncedLoad();
    },
    isExcelDownloading() {
      if (!this.isExcelDownloading) return;

      this.downloadExcel({
        fileName: this.exportTitle + ".xlsx",
        sheets: [
          {
            name: this.exportTitle,
            headers: this.headers,
            items: this.translatedInventory.map((item, index) =>
              this.formatItemForPrint(item, index)
            ),
            title: this.exportTitle,
            subtitle: this.subtitle,
          },
        ],
      });
    },
    isPrinting(isPrinting) {
      if (!isPrinting) return;

      const items = this.translatedInventory
        .filter((item) => this.stockType === "required" || item.order)
        .map((item, index) => this.formatItemForPrint(item, index));

      this.printDocument({
        title: this.exportTitle,
        subtitle: this.subtitle,
        tables: [{ headers: this.headers, items }],
      });
    },
  },
};
</script>
